// WSDWebSocket.js — AnyCable-backed (no custom retry), same public API
import { createCable } from "@anycable/web";
import LocalStorage from "../Generic/LocalStorage";

class WSDWebSocket {
  constructor() {
    this.cable = null;
    this.open = false;

    this.dataCallbacks = []; // [{ name, callback }]
    this.subscriptions = new Map(); // channelName -> subscription
    this.subscribedChannels = new Set(); // channel names to re-subscribe
  }

  connect() {
    const endpoint =
      process.env.REACT_APP_WEB_SOCKET_URL +
      LocalStorage.getAuthenticationToken();

    // Clean up any previous connection
    this.disconnect();

    this.cable = createCable(endpoint);

    // Kick off the connection (AnyCable handles retry internally)
    this.cable
      .connect()
      .then(() => {
        this.open = true;
        this.#connectToChannelsIfNeeded();
      })
      .catch((err) => {
        console.error("AnyCable connect() failed:", err);
        this.open = false;
      });

    // Lifecycle hooks
    this.cable.on("connect", () => {
      this.open = true;
      this.#connectToChannelsIfNeeded();
    });

    this.cable.on("disconnect", () => {
      this.open = false;
      // No special retry/backoff — AnyCable will handle reconnects
      this.#removeAllSubscriptions(); // local cleanup; subs will be recreated on reconnect
    });

    this.cable.on("error", (err) => {
      console.error("AnyCable error:", err);
    });
  }

  disconnect() {
    this.open = false;

    if (this.cable) {
      try {
        this.cable.disconnect();
      } catch (_) {}
      this.cable = null;
    }

    this.#removeAllSubscriptions();
  }

  subscribeToChannel(name, callback) {
    // keep only one callback per channel
    const existing = this.dataCallbacks.find((i) => i.name === name);
    if (!existing) this.dataCallbacks.push({ name, callback });

    this.subscribedChannels.add(name);

    if (this.open && this.cable && this.cable.state === "connected") {
      this.#connectToChannel(name);
    }
  }

  // ===== private =====

  #connectToChannelsIfNeeded() {
    if (!this.open || !this.cable || this.cable.state !== "connected") return;
    this.subscribedChannels.forEach((name) => this.#connectToChannel(name));
  }

  #connectToChannel(name) {
    if (!this.open || !this.cable || this.cable.state !== "connected") return;
    if (this.subscriptions.has(name)) return; // already subscribed

    const sub = this.cable.subscribeTo(name);

    sub.on("message", (data) => {
      console.log("data", data);
      this.#handleMessageForChannel(name, data);
    });

    // keep local map in sync
    sub.on("disconnect", () => this.subscriptions.delete(name));
    sub.on("reject", () => {
      console.warn(`Subscription rejected: ${name}`);
      this.subscriptions.delete(name);
    });

    this.subscriptions.set(name, sub);
  }

  #handleMessageForChannel(channel, message) {
    const item = this.dataCallbacks.find((obj) => obj.name === channel);
    if (item && typeof item.callback === "function") {
      // match old signature: (channelName, message)
      item.callback(channel, message);
    }
  }

  #removeAllSubscriptions() {
    this.subscriptions.clear();
  }
}

export default WSDWebSocket;
